import React from 'react';
import { useTheme } from '@mui/material/styles';
import { getTextColor } from '../../utils/colorUtils';

const StyledChip = ({
    children,
    label,
    color,
    variant = 'filled', // 'filled' | 'outlined'
    size = 'small', // 'small' | 'medium' | 'large'
    icon,
    onClick,
    disabled = false,
    className,
    style = {},
    ...props
}) => {
    const theme = useTheme();

    const lightMode = theme.palette.mode === 'light';
    // Use children if provided, otherwise use label
    const content = children || label;

    // Default color if none provided
    const chipColor = color || theme.palette.primary.main;

    // Size variations
    const sizeConfig = {
        small: {
            padding: '4px 8px',
            fontSize: '0.75rem',
            borderRadius: '16px',
            gap: '4px',
            iconSize: '14px'
        },
        medium: {
            padding: '6px 12px',
            fontSize: '0.875rem',
            borderRadius: '18px',
            gap: '6px',
            iconSize: '16px'
        },
        large: {
            padding: '8px 16px',
            fontSize: '1rem',
            borderRadius: '20px',
            gap: '8px',
            iconSize: '18px'
        }
    };

    const sizeStyles = sizeConfig[size];

    // Base chip styles
    const chipStyles = {
        display: 'inline-flex',
        alignItems: 'center',
        padding: sizeStyles.padding,
        borderRadius: sizeStyles.borderRadius,
        fontSize: sizeStyles.fontSize,
        fontWeight: '500',
        border: '1px solid',
        cursor: onClick && !disabled ? 'pointer' : 'default',
        position: 'relative',
        gap: sizeStyles.gap,
        transition: 'all 0.2s ease-in-out',
        userSelect: 'none',
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        // Combine with custom styles
        ...style
    };

    // Get chip style based on variant
    const getChipStyle = (color, variant, lightMode) => ({
        ...chipStyles,
        backgroundColor: variant === 'filled' ? color : 'transparent',
        color: variant === 'filled' ? getTextColor(color) : lightMode ? '#000' : '#FFF',
        borderColor: color,
        '&:hover': onClick && !disabled ? {
            opacity: 0.8,
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[2]
        } : {}
    });

    const finalStyles = getChipStyle(chipColor, variant, lightMode);

    const handleClick = (event) => {
        if (onClick && !disabled) {
            onClick(event);
        }
    };

    // Clone icon with appropriate size if provided
    const renderIcon = () => {
        if (!icon) return null;

        return React.cloneElement(icon, {
            style: {
                fontSize: sizeStyles.iconSize,
                ...icon.props.style
            }
        });
    };

    return (
        <div
            className={className}
            style={finalStyles}
            onClick={handleClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick && !disabled ? 0 : undefined}
            onKeyDown={(e) => {
                if (onClick && !disabled && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleClick(e);
                }
            }}
            {...props}
        >
            {renderIcon()}
            {content}
        </div>
    );
};

export default StyledChip;